import {
  CLCustomButton,
  CLCustomInput,
  CLSecondaryCustomButton
} from '@/components/ui-controls';
import CLTooltip from '@/components/utils/tooltip';
import { GetPublicRunsByChecklistId } from '@/graphql/overview.gql';
import {
  shareOnFacebook,
  shareOnLinkedin,
  shareOnReddit,
  shareOnTelegram,
  shareOnTwitter,
  shareOnWhatsapp
} from '@/services/utils/social-share.service';
import {
  copyToClipboard,
  getChecklistCleanURL,
  openInNewTab
} from '@/services/utils/utils.service';
import { useAnalyticsRowSelectionStore } from '@/stores/responses/responses-row-selection.store';
import { Checklist } from '@/types/checklist.type';
import { PublicRun, PublicRuns } from '@/types/public-run.type';
import { useQuery } from '@apollo/client';
import { Dialog, Transition } from '@headlessui/react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { Dispatch, Fragment, SetStateAction, useEffect, useState } from 'react';
import {shallow} from 'zustand/shallow';

interface PopupProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  checklistId: string;
  checklist: Checklist;
}

export default function ThankyouPopupComponent({
  isOpen,
  setIsOpen,
  checklistId,
  checklist
}: PopupProps) {
  // Variables

  //State
  const [url, setUrl] = useState<string>('');
  const [isCopied, setIsCopied] = useState(false);

  // Stores
  const [selectedRun, setSelectedRun] = useAnalyticsRowSelectionStore(
    s => [s.selectedRun, s.setSelectedRun],
    shallow
  );
  // GraphQL
  const { loading, data } = useQuery<PublicRuns>(GetPublicRunsByChecklistId, {
    variables: {
      id: checklistId
    },
    fetchPolicy: 'cache-first'
  });

  //useEffect
  useEffect(() => {
    if (data?.public_runs?.[0]) {
      setSelectedRun(data.public_runs[0] as PublicRun, true, false);
    }
  }, [data, setSelectedRun]);

  useEffect(() => {
    if (selectedRun) {
      const protocol =
        window?.location?.hostname === 'localhost' ? 'http://' : 'https://';
      const host = window?.location?.host || 'app.checklist.gg';
      const path = getChecklistCleanURL(
        selectedRun.id as string,
        selectedRun.name || ''
      );

      setUrl(`${protocol}${host}/${path}?runId=true`);
    }
  }, [selectedRun]);

  const socialShareOptions = [
    {
      id: 'twitter',
      tooltip: 'Share on Twitter',
      icon: `
        <svg style="width: 14px;" viewBox="0 0 16 13" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M16 1.539C15.405 1.8 14.771 1.973 14.11 2.057C14.79 1.651 15.309 1.013 15.553 0.244C14.919 0.622 14.219 0.889 13.473 1.038C12.871 0.397 12.013 0 11.077 0C9.261 0 7.799 1.474 7.799 3.281C7.799 3.541 7.821 3.791 7.875 4.029C5.148 3.896 2.735 2.589 1.114 0.598C0.831 1.089 0.665 1.651 0.665 2.256C0.665 3.392 1.25 4.399 2.122 4.982C1.595 4.972 1.078 4.819 0.64 4.578C0.64 4.588 0.64 4.601 0.64 4.614C0.64 6.208 1.777 7.532 3.268 7.837C3.001 7.91 2.71 7.945 2.408 7.945C2.198 7.945 1.986 7.933 1.787 7.889C2.212 9.188 3.418 10.143 4.852 10.174C3.736 11.047 2.319 11.573 0.785 11.573C0.516 11.573 0.258 11.561 0 11.528C1.453 12.465 3.175 13 5.032 13C11.068 13 14.368 8 14.368 3.666C14.368 3.521 14.363 3.381 14.356 3.242C15.007 2.78 15.554 2.203 16 1.539Z"
                fill="black" />
        </svg>`,
      onClick: (title: string, url: string) => {
        shareOnTwitter(title, url, true);
      }
    },
    {
      id: 'reddit',
      tooltip: 'Share on Reddit',
      icon: `
        <svg style="width: 16px;" viewBox="0 0 16 14" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M15.9998 7.10479C15.9874 6.77188 15.8775 6.44959 15.6833 6.1768C15.4892 5.904 15.219 5.69233 14.9054 5.56732C14.5918 5.4423 14.2482 5.40928 13.9159 5.47222C13.5837 5.53517 13.2771 5.6914 13.033 5.92208C11.6666 5.0075 10.0604 4.50223 8.40857 4.46734L9.18931 0.777279L11.7598 1.3095C11.7903 1.58919 11.9211 1.849 12.1287 2.04229C12.3362 2.23558 12.607 2.34969 12.8923 2.36415C13.1776 2.3786 13.4588 2.29245 13.6854 2.12115C13.912 1.94986 14.0692 1.70464 14.1288 1.42951C14.1883 1.15439 14.1463 0.867371 14.0103 0.619985C13.8743 0.372598 13.6532 0.181041 13.3867 0.0796996C13.1202 -0.0216422 12.8258 -0.0261327 12.5562 0.0670341C12.2866 0.160201 12.0596 0.344924 11.9159 0.588045L8.97311 0.0085164C8.92498 -0.00187996 8.87523 -0.00276719 8.82675 0.00590611C8.77827 0.0145794 8.73202 0.0326408 8.69068 0.0590434C8.64935 0.085446 8.61374 0.119665 8.58593 0.159717C8.55812 0.199768 8.53866 0.244856 8.52868 0.292367L7.63984 4.39638C5.9677 4.4212 4.33942 4.92685 2.95538 5.85111C2.77021 5.67956 2.54938 5.54971 2.30818 5.47055C2.06698 5.39139 1.81118 5.36482 1.55852 5.39268C1.30585 5.42054 1.06236 5.50216 0.844914 5.63189C0.627469 5.76162 0.441272 5.93635 0.299226 6.14398C0.15718 6.35161 0.0626826 6.58716 0.0222821 6.83433C-0.0181184 7.08149 -0.00345558 7.33435 0.0652544 7.57537C0.133964 7.8164 0.255078 8.03984 0.420203 8.23019C0.585327 8.42055 0.790513 8.57328 1.02154 8.67779C1.00802 8.851 1.00802 9.02498 1.02154 9.19819C1.02154 11.8475 4.15652 14 8.0242 14C11.8919 14 15.0269 11.8475 15.0269 9.19819C15.0404 9.02498 15.0404 8.851 15.0269 8.67779C15.3233 8.53273 15.5719 8.30804 15.7441 8.02968C15.9163 7.75132 16.0049 7.43067 15.9998 7.10479V7.10479ZM3.98836 8.2875C3.98836 8.05358 4.05881 7.82492 4.19079 7.63042C4.32278 7.43592 4.51037 7.28433 4.72985 7.19482C4.94933 7.1053 5.19084 7.08188 5.42384 7.12751C5.65684 7.17315 5.87086 7.28579 6.03884 7.4512C6.20683 7.6166 6.32122 7.82734 6.36757 8.05676C6.41392 8.28619 6.39013 8.52399 6.29922 8.7401C6.20831 8.95622 6.05435 9.14093 5.85683 9.27089C5.6593 9.40085 5.42707 9.47021 5.18951 9.47021C5.03177 9.47021 4.87558 9.43962 4.72985 9.38018C4.58412 9.32075 4.45171 9.23363 4.34017 9.1238C4.22863 9.01398 4.14016 8.8836 4.0798 8.7401C4.01943 8.59661 3.98836 8.44282 3.98836 8.2875V8.2875ZM10.967 11.54C10.1148 12.1723 9.06633 12.4941 8.00018 12.4506C6.93403 12.4941 5.88554 12.1723 5.03336 11.54C4.98228 11.4787 4.95618 11.4009 4.96013 11.3217C4.96408 11.2425 4.9978 11.1676 5.05473 11.1116C5.11165 11.0555 5.18772 11.0223 5.26814 11.0184C5.34855 11.0145 5.42756 11.0402 5.48979 11.0905C6.21195 11.6121 7.09346 11.875 7.98817 11.8356C8.88398 11.8836 9.76959 11.6291 10.4986 11.1142C10.5623 11.053 10.648 11.0193 10.737 11.0204C10.7811 11.0209 10.8246 11.03 10.8651 11.0471C10.9056 11.0642 10.9422 11.089 10.973 11.1201C11.0038 11.1512 11.028 11.1879 11.0444 11.2282C11.0607 11.2685 11.0688 11.3115 11.0683 11.3549C11.0677 11.3983 11.0585 11.4411 11.0411 11.481C11.0237 11.5209 10.9985 11.557 10.967 11.5873V11.54ZM10.7508 9.51752C10.5132 9.51752 10.281 9.44816 10.0835 9.3182C9.88595 9.18824 9.732 9.00353 9.64108 8.78741C9.55017 8.5713 9.52639 8.3335 9.57273 8.10407C9.61908 7.87465 9.73348 7.66391 9.90146 7.49851C10.0694 7.3331 10.2835 7.22046 10.5165 7.17482C10.7495 7.12919 10.991 7.15261 11.2105 7.24213C11.4299 7.33164 11.6175 7.48323 11.7495 7.67773C11.8815 7.87222 11.9519 8.10089 11.9519 8.33481C11.9585 8.49506 11.9318 8.65494 11.8736 8.80474C11.8154 8.95453 11.7269 9.09111 11.6134 9.20616C11.5 9.32122 11.3639 9.41234 11.2135 9.474C11.0632 9.53566 10.9017 9.56656 10.7388 9.56483L10.7508 9.51752Z"
                fill="black" />
        </svg>
      `,
      onClick: (title: string, url: string) => {
        shareOnReddit(title, url, true);
      }
    },
    {
      id: 'facebook',
      tooltip: 'Share on Facebook',
      icon: `
        <svg style="width: 14px;" viewBox="0 0 16 16" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M11.9994 0.00332907L9.92456 0C7.59357 0 6.08719 1.54552 6.08719 3.93762V5.75313H4.00105C3.82078 5.75313 3.6748 5.89927 3.6748 6.07954V8.71001C3.6748 8.89028 3.82095 9.03626 4.00105 9.03626H6.08719V15.6738C6.08719 15.854 6.23317 16 6.41343 16H9.13525C9.31552 16 9.46149 15.8539 9.46149 15.6738V9.03626H11.9007C12.0809 9.03626 12.2269 8.89028 12.2269 8.71001L12.2279 6.07954C12.2279 5.99299 12.1935 5.91009 12.1324 5.84884C12.0713 5.78758 11.9881 5.75313 11.9015 5.75313H9.46149V4.2141C9.46149 3.47438 9.63777 3.09886 10.6014 3.09886L11.999 3.09836C12.1791 3.09836 12.3251 2.95222 12.3251 2.77211V0.329578C12.3251 0.149642 12.1793 0.00366197 11.9994 0.00332907Z"
                fill="black" />
        </svg>
      `,
      onClick: (title: string, url: string) => {
        shareOnFacebook(title, url, true);
      }
    },
    {
      id: 'linkedin',
      tooltip: 'Share on LinkedIn',
      icon: `
        <svg style="width: 14px;" viewBox="0 0 16 16" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M1.94035 0C0.767098 0 0 0.770419 0 1.78303C0 2.77327 0.744246 3.56566 1.89533 3.56566H1.9176C3.1138 3.56566 3.85824 2.77327 3.85824 1.78303C3.83588 0.770419 3.1138 0 1.94035 0V0Z"
                fill="black" />
            <path d="M0.202881 4.97461H3.6328V15.2937H0.202881V4.97461Z" fill="black" />
            <path
                d="M12.0513 4.73242C10.201 4.73242 8.96029 6.47111 8.96029 6.47111V4.97461H5.53027V15.2937H8.96009V9.5311C8.96009 9.22261 8.98246 8.91459 9.07308 8.69399C9.32103 8.07796 9.8853 7.43977 10.8329 7.43977C12.074 7.43977 12.5704 8.38607 12.5704 9.77329V15.2937H16V9.3769C16 6.20734 14.3078 4.73242 12.0513 4.73242V4.73242Z"
                fill="black" />
        </svg>
                                        `,
      onClick: (title: string, url: string) => {
        shareOnLinkedin(title, url, true);
      }
    },
    {
      id: 'whatsapp',
      tooltip: 'Share on WhatsApp',
      icon: `
        <svg style="width: 14px;" viewBox="0 0 16 16" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M15.714 13.8553C15.3881 14.8439 14.0945 15.6639 13.0626 15.9033C12.3567 16.0648 11.4347 16.1936 8.33071 14.8113C4.36041 13.0445 1.80361 8.71081 1.60433 8.42964C1.4135 8.14847 0 6.13494 0 4.05247C0 1.97 0.984554 0.955977 1.38141 0.520617C1.70735 0.16326 2.24607 0 2.76283 0C2.93002 0 3.08032 0.0090701 3.21542 0.0163261C3.61228 0.0344661 3.81156 0.0598624 4.07332 0.732855C4.39925 1.57636 5.19297 3.65883 5.28754 3.87288C5.3838 4.08694 5.48006 4.37717 5.34496 4.65834C5.2183 4.94858 5.10684 5.07738 4.90757 5.32408C4.70829 5.57078 4.51915 5.75944 4.31988 6.02428C4.13749 6.25466 3.93146 6.50137 4.16113 6.92765C4.3908 7.34487 5.18453 8.73621 6.35316 9.85363C7.86123 11.2958 9.0839 11.7565 9.52129 11.9524C9.84722 12.0975 10.2356 12.0631 10.4738 11.791C10.776 11.4409 11.1493 10.8604 11.5292 10.289C11.7994 9.87903 12.1406 9.82823 12.4986 9.97335C12.8634 10.1094 14.7936 11.1343 15.1905 11.3465C15.5874 11.5606 15.8491 11.6622 15.9454 11.8418C16.0399 12.0214 16.0399 12.8649 15.714 13.8553Z"
                fill="black" />
        </svg>
      `,
      onClick: (title: string, url: string) => {
        shareOnWhatsapp(title, url, true);
      }
    },
    {
      id: 'telegram',
      tooltip: 'Share on Telegram',
      icon: `
        <svg style="width: 14px;" viewBox="0 0 16 13" fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
                d="M1.09992 5.59647C5.39487 3.76188 8.25885 2.55241 9.69185 1.96805C13.7834 0.299594 14.6335 0.00976514 15.1877 9.27819e-05C15.3095 -0.00191006 15.582 0.0277027 15.7586 0.16813C15.9076 0.286703 15.9486 0.44688 15.9683 0.5593C15.9879 0.671721 16.0123 0.927819 15.9929 1.12793C15.7712 3.41192 14.8118 8.95456 14.3237 11.5127C14.1172 12.5951 13.7105 12.958 13.3168 12.9936C12.4613 13.0707 11.8116 12.4392 10.9829 11.9067C9.68624 11.0733 8.95369 10.5545 7.69503 9.74136C6.24042 8.80157 7.18338 8.28505 8.01236 7.44091C8.22931 7.21999 11.999 3.85836 12.0719 3.55341C12.0811 3.51527 12.0895 3.37311 12.0034 3.29804C11.9172 3.22297 11.7901 3.24864 11.6983 3.26906C11.5683 3.29799 9.4968 4.64035 5.48389 7.29611C4.89591 7.69195 4.36333 7.88482 3.88616 7.87472C3.36012 7.86357 2.34822 7.58311 1.59598 7.34338C0.673328 7.04933 -0.0599784 6.89387 0.00387615 6.3945C0.0371355 6.13439 0.402482 5.86838 1.09992 5.59647Z"
                fill="black" />
        </svg>
      `,
      onClick: (title: string, url: string) => {
        shareOnTelegram(title, url, true);
      }
    }
  ];

  // States

  return (
    <>
      <Head>
        {/* Load turnstile captcha */}
        <script
          src="https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback"
          async
          defer
        ></script>
      </Head>

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            // setIsOpen(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-70" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-6 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-lg bg-white p-4 text-left align-middle shadow-xl transition-all">
                  {/* Body */}
                  <div className="flex flex-col gap-10 sm:p-6 md:p-10 justify-center items-center">
                    {/* Title */}
                    <div className="text-center flex flex-col gap-2 items-center justify-center">
                      <h1 className="text-xl md:text-2xl lg:text-3xl">
                        Thank you for your time!
                      </h1>
                    </div>

                    {/* Shareable link */}
                    <div className="flex flex-col gap-2 items-center bg-[#FEF9F4] rounded-lg p-4 w-full max-w-lg">
                      <div className="flex">
                        <span className="text-sm whitespace-nowrap">
                          Shareable Link
                        </span>
                      </div>
                      <div className="flex w-full">
                        <CLCustomInput
                          type="text"
                          className="w-full border-r-0 rounded-l-lg"
                          value={url || 'Loading...'}
                          disabled
                        />

                        {/* Open in new tab */}
                        <CLTooltip content="Open in new tab">
                          <CLCustomButton
                            className="flex justify-center items-center px-4 py-2 border-[1px] border-black/10 border-r-0 bg-white"
                            onClick={() => {
                              openInNewTab(url);
                            }}
                          >
                            <svg
                              viewBox="0 0 15 15"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ width: '16px', height: '24px' }}
                            >
                              <path
                                d="M1.98235 14.5C1.57059 14.5 1.22059 14.3559 0.932353 14.0676C0.644118 13.7794 0.5 13.4294 0.5 13.0176V1.98235C0.5 1.57059 0.644118 1.22059 0.932353 0.932353C1.22059 0.644118 1.57059 0.5 1.98235 0.5H6.57353C6.73824 0.5 6.88235 0.558196 7.00588 0.674588C7.12941 0.791529 7.19118 0.939216 7.19118 1.11765C7.19118 1.29608 7.12941 1.44349 7.00588 1.55988C6.88235 1.67682 6.73824 1.73529 6.57353 1.73529H1.98235C1.92745 1.73529 1.87255 1.76275 1.81765 1.81765C1.76275 1.87255 1.73529 1.92745 1.73529 1.98235V13.0176C1.73529 13.0725 1.76275 13.1275 1.81765 13.1824C1.87255 13.2373 1.92745 13.2647 1.98235 13.2647H13.0176C13.0725 13.2647 13.1275 13.2373 13.1824 13.1824C13.2373 13.1275 13.2647 13.0725 13.2647 13.0176V8.42647C13.2647 8.26176 13.3229 8.11765 13.4393 7.99412C13.5562 7.87059 13.7039 7.80882 13.8824 7.80882C14.0608 7.80882 14.2085 7.87059 14.3254 7.99412C14.4418 8.11765 14.5 8.26176 14.5 8.42647V13.0176C14.5 13.4294 14.3559 13.7794 14.0676 14.0676C13.7794 14.3559 13.4294 14.5 13.0176 14.5H1.98235ZM5.19412 9.80588C5.07059 9.69608 5.00882 9.55525 5.00882 9.38341C5.00882 9.21212 5.07059 9.06471 5.19412 8.94118L12.4 1.73529H9.76471C9.58627 1.73529 9.43886 1.67682 9.32247 1.55988C9.20553 1.44349 9.14706 1.29608 9.14706 1.11765C9.14706 0.939216 9.20553 0.791529 9.32247 0.674588C9.43886 0.558196 9.58627 0.5 9.76471 0.5H13.7588C13.9647 0.5 14.1398 0.571922 14.2842 0.715765C14.4281 0.860157 14.5 1.03529 14.5 1.24118V5.23529C14.5 5.41373 14.4418 5.56114 14.3254 5.67753C14.2085 5.79447 14.0608 5.85294 13.8824 5.85294C13.7039 5.85294 13.5562 5.79447 13.4393 5.67753C13.3229 5.56114 13.2647 5.41373 13.2647 5.23529V2.6L6.03824 9.82647C5.92843 9.95 5.79118 10.0118 5.62647 10.0118C5.46177 10.0118 5.31765 9.94314 5.19412 9.80588Z"
                                fill="#171717"
                              ></path>
                            </svg>
                          </CLCustomButton>
                        </CLTooltip>

                        {/* Copy url */}
                        <CLTooltip content={isCopied ? 'Copied' : 'Copy URL'}>
                          <CLCustomButton
                            className="flex justify-center items-center px-4 py-2 border-[1px] border-black/10 rounded-r-lg bg-white"
                            onClick={() => {
                              copyToClipboard(url);
                              setIsCopied(true);
                              setTimeout(() => setIsCopied(false), 1000);
                            }}
                          >
                            {isCopied ? (
                              // ✓ Tick icon
                              <svg
                                viewBox="0 0 17 17"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ width: '14px', height: '24px' }}
                              >
                                <path
                                  fill="#171717"
                                  d="M7.629 13.636l-4.243-4.242L2 10.78l5.629 5.627L18 6.036l-1.386-1.386z"
                                />
                              </svg>
                            ) : (
                              <svg
                                viewBox="0 0 14 17"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ width: '14px', height: '24px' }}
                              >
                                <path
                                  d="M1.62581 16.7097C1.17419 16.7097 0.790322 16.5516 0.474193 16.2355C0.158064 15.9194 0 15.5355 0 15.0839V3.6129H1.35484V15.0839C1.35484 15.1591 1.38103 15.223 1.43342 15.2754C1.48641 15.3283 1.55054 15.3548 1.62581 15.3548H10.3871V16.7097H1.62581ZM4.7871 13.5484C4.33548 13.5484 3.95161 13.3903 3.63548 13.0742C3.31935 12.7581 3.16129 12.3742 3.16129 11.9226V1.64839C3.16129 1.18172 3.31935 0.790323 3.63548 0.474194C3.95161 0.158065 4.33548 0 4.7871 0H12.3516C12.8183 0 13.2097 0.158065 13.5258 0.474194C13.8419 0.790323 14 1.18172 14 1.64839V11.9226C14 12.3742 13.8419 12.7581 13.5258 13.0742C13.2097 13.3903 12.8183 13.5484 12.3516 13.5484H4.7871ZM4.7871 12.1935H12.3516C12.4269 12.1935 12.4946 12.1671 12.5548 12.1141C12.6151 12.0617 12.6452 11.9978 12.6452 11.9226V1.64839C12.6452 1.57312 12.6151 1.50538 12.5548 1.44516C12.4946 1.38495 12.4269 1.35484 12.3516 1.35484H4.7871C4.71183 1.35484 4.648 1.38495 4.59561 1.44516C4.54262 1.50538 4.51613 1.57312 4.51613 1.64839V11.9226C4.51613 11.9978 4.54262 12.0617 4.59561 12.1141C4.648 12.1671 4.71183 12.1935 4.7871 12.1935ZM4.51613 12.1935V1.35484V12.1935Z"
                                  fill="#171717"
                                ></path>
                              </svg>
                            )}
                          </CLCustomButton>
                        </CLTooltip>
                      </div>
                    </div>

                    {/* Social share */}
                    <div className="flex flex-col gap-2 items-center justify-center">
                      <span className="text-sm">Spread the word</span>

                      {/* List of icons */}
                      <div className="flex flex-wrap gap-2 justify-center items-center">
                        {socialShareOptions.map((socialShareOption, index) => (
                          <div key={socialShareOption.id}>
                            <CLSecondaryCustomButton
                              className="rounded-full h-[34px] w-[34px] p-2 flex items-center justify-center"
                              dangerouslySetInnerHTML={{
                                __html: socialShareOption?.icon || ''
                              }}
                              onClick={() => {
                                const title = encodeURI(
                                  checklist?.title || 'Untitled'
                                );
                                const url = encodeURI(window.location.href);
                                socialShareOption.onClick(title, url);
                              }}
                            ></CLSecondaryCustomButton>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Powered by */}
                    <div className="flex flex-col gap-2 items-center justify-center">
                      <span className="text-sm">Powered by</span>

                      {/* Logo */}
                      <div>
                        <Link href="https://checklist.gg">
                          <Image
                            height="38"
                            width="160"
                            className="w-40"
                            src="/images/logo/checklist.svg"
                            alt="checklist.gg"
                          />
                        </Link>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
